import { Close } from "@mui/icons-material";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Collapse,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { FC, Fragment, useContext, useEffect, useState } from "react";
import { PrescriptionContext } from ".";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddPrescription from "./AddPrescription";
import { DrugType } from "./types";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useMutation } from "react-query";
import { deletePrescription, getPrescription } from "../../services";
import usePopup from "../../../hooks/usePopup";
import { useParams } from "react-router-dom";
import { HL7ContexTriggerContex } from "../../../context/HL7MessageTrigger/HL7MessageTriggerProvider";
import EmptyListAlert from "../layouts/EmptyListAlert";
import HistoryComponent from "../layouts/HistoryComponent";

const PrescriptionList: FC = () => {
  const { setTrigger } = useContext(HL7ContexTriggerContex);
  const { encounterId } = useParams();
  const { showSnackbar } = usePopup();
  const { list, setList, edit, setEdit } = useContext(PrescriptionContext);
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(-1);
  const handleClose = () => {
    setShow(false);
  };

  const {
    mutate: fetchPrescription,
    // isLoading:presciptionLoading
  } = useMutation(getPrescription, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        setList(result.response);
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
        });
      }
    },
  });

  const { mutate: prescriptionDelete, isLoading: deleteLoading } = useMutation(
    deletePrescription,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
          });
          setTrigger({
            encounterId: encounterId as string,
            tab: "prescriptions",
          });
          fetchPrescription(encounterId as string);
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
          });
        }
      },
      onError: () => {
        showSnackbar({
          message: "Something went wrong !",
          open: true,
          variant: "warning",
        });
      },
    }
  );

  const handleDelete = (id: number, index: number) => {
    prescriptionDelete({ encounterId: parseInt(encounterId as string), id });
    setSelected(index);
  };

  useEffect(() => {
    fetchPrescription(encounterId as string);
  }, []);
  return (
    <TableContainer component={Paper}>
      <Table size="small" sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow
            sx={(theme) => ({ backgroundColor: theme.palette.primary.main })}
          >
            {heading.map((heading, index) => (
              <TableCell
                sx={(theme) => ({
                  color: theme.palette.background.paper,
                  textTransform: "uppercase",
                })}
                align="left"
                key={`heading${index}`}
              >
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ padding: 0 }} colSpan={heading.length}>
              <EmptyListAlert
                alert="No prescription"
                isData={Boolean(list?.length || show)}
                children={
                  !show && (
                    <div className="w-full flex justify-end items-center p-2 gap-3">
                      <Button
                        color="success"
                        endIcon={<AddCircleIcon />}
                        onClick={() => {
                          setShow(true);
                          setEdit(null);
                        }}
                      >
                        add
                      </Button>
                      <div>
                        <HistoryComponent
                          logProps={{field:"prescription",title:"Prescription"}}
                          title="Prescription"
                          tableprops={tableProps}
                        />
                      </div>
                    </div>
                  )
                }
              />
              <Collapse in={show} timeout="auto" unmountOnExit>
                <Box>
                  <AddPrescription close={handleClose} />
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
          {list?.map((row, index: number) => (
            <Fragment key={`allergy${index}`}>
              <TableRow>
                <TableCell component="th" scope="row">
                  <strong>{(row.drug as DrugType).drugPackageName}</strong>
                  ,&nbsp;
                  {(row.drug as DrugType).drugGenericName}
                </TableCell>
                <TableCell>
                  {row.prescriptionDuration}{" "}
                  {parseInt(row.prescriptionDuration) > 1 ? "days" : "day"}
                </TableCell>
                <TableCell>{row.prescriptionQuantity}</TableCell>
                <TableCell>{row.prescriptionRemarks}</TableCell>
                <TableCell>{row.refill}</TableCell>
                <TableCell width={105} sx={{}}>
                  {moment(row.startDate as string).format("DD-MM-YYYY")}
                </TableCell>
                <TableCell>
                  <div className="flex flex-row">
                    {Boolean(edit) &&
                    edit?.prescriptionId == row.prescriptionId ? (
                      <IconButton
                        color="error"
                        onClick={() => {
                          setEdit(null);
                        }}
                      >
                        <Close />
                      </IconButton>
                    ) : (
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setEdit(row);
                          setShow(false);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {deleteLoading && selected == index ? (
                      <CircularProgress size={18} color="error" />
                    ) : (
                      <IconButton
                        color="error"
                        onClick={() => {
                          handleDelete(row.prescriptionId, index);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ padding: 0 }} colSpan={heading.length}>
                  <Collapse
                    in={
                      Boolean(edit) &&
                      edit?.prescriptionId == row.prescriptionId
                    }
                    timeout="auto"
                    unmountOnExit
                  >
                    <AddPrescription close={handleClose} />
                  </Collapse>
                </TableCell>
              </TableRow>
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PrescriptionList;

const heading = [
  "package name",
  "duration",
  "quantity",
  "instruction",
  "refill",
  "date",
  "options",
];
const tableProps = [
  { heading: "package name", key: "drug.drugPackageName" },
  { heading: "duration", key: "prescriptionDuration" },
  { heading: "quantity", key: "prescriptionQuantity" },
  { heading: "instruction", key: "prescriptionRemarks" },
  { heading: "refill", key: "refill" },
  { heading: "date", key: "startDate" },
];
